"use client";

import React, { useState } from "react";
import { Minus, Plus, Tag, Folder } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  VariableProductAddToCart,
  ProductPrice,
  EcommerceProduct,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function RelatedProductsConsumer({ currentProductId }: { currentProductId: number }) {
  const { products, loading } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="aspect-square bg-muted/30 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  const filteredProducts = products.filter((p) => p.id !== currentProductId).slice(0, 4);

  if (filteredProducts.length === 0) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
        {[1, 2, 3, 4].map((i) => (
          <Link key={i} data-index={i - 1} to="/ShopPage" className="group block">
            <div className="aspect-square overflow-hidden bg-card border border-border mb-4 rounded-lg shadow-md">
              <div className="h-full w-full flex items-center justify-center text-green-900">
                <span className="font-default text-lg">Forever Fresh</span>
              </div>
            </div>
            <h3 className="font-default text-lg md:text-xl font-bold text-green-950 mb-2 group-hover:text-green-700 transition-colors">
              Awareness Item
            </h3>
            <div className="text-sm text-green-800 font-default">
              Support Now
            </div>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
      {filteredProducts.map((product) => (
        <Link key={product.id} to={product.link} className="group block">
          <div className="aspect-square overflow-hidden bg-card border border-border mb-4 rounded-lg shadow-md">
            <img
              src={product.featured_image_url}
              alt={product.title}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <h3 className="font-default text-lg md:text-xl font-bold text-green-950 mb-2 group-hover:text-green-700 transition-colors">
            {product.title}
          </h3>
          <ProductPrice product={product} className="text-sm text-green-800 font-default" />
        </Link>
      ))}
    </div>
  );
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariation, setSelectedVariation] = useState<EcommerceProduct | null>(null);
  
  const currentWPQueryParams = typeof window !== "undefined" && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : {};
  const productId = currentWPQueryParams.p || currentWPQueryParams.product_id;
  const product = selectedVariation || (products.find(p => p.id === Number(productId)) || products[0]);
  console.log("Debug: currentWPQueryParams, productId, product.id", currentWPQueryParams, productId, product?.id);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square bg-muted/30 animate-pulse rounded-lg shadow-md" />
        <div className="space-y-6">
          <div className="h-12 bg-muted/30 animate-pulse rounded-lg w-3/4" />
          <div className="h-8 bg-muted/30 animate-pulse rounded-lg w-1/3" />
          <div className="h-24 bg-muted/30 animate-pulse rounded-lg" />
          <div className="h-12 bg-muted/30 animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="text-center py-20">
        <p className="text-foreground">Product currently unavailable.</p>
      </div>
    );
  }
  console.log("Debug: product object, ID, images array, length, featured_image_url, keys", product, product?.id, product?.images, product?.images?.length, product?.featured_image_url, Object.keys(product || {}));

  const hasDiscount = product.discounted_price !== undefined && product.discounted_price > 0;
  const images = (Array.isArray(product.images) && product.images.length > 0) ? product.images.map((img: any) => img.src || img.url || img) : (product.featured_image_url ? [product.featured_image_url] : []);
  console.log("Debug: raw product.images, processed images array, count, display thumbnails", product?.images, images, images.length, images.length > 1);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        <div className="relative">
          <div className="sticky top-24 space-y-4">
            <div className="aspect-square overflow-hidden bg-card rounded-lg shadow-lg border border-border">
              <img
                src={images[selectedImageIndex] || product.featured_image_url}
                alt={product.title}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              {hasDiscount && (
                <Badge className="absolute top-4 left-4 bg-green-600 text-white rounded-lg">
                  Limited Drop
                </Badge>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img: string, idx: number) => (
                  <Button
                    key={idx}
                    variant="ghost"
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`size-20 shrink-0 rounded-lg border-2 overflow-hidden p-0 ${selectedImageIndex === idx ? "border-green-700" : "border-transparent hover:border-green-700"}`}
                  >
                    <img src={img} alt={`${product.title} thumbnail ${idx + 1}`} className="h-full w-full object-cover" />
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col">
          {product.categories && product.categories.length > 0 && (
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-green-800 mb-4 font-default">
              {product.categories.slice(0, 1).map((cat, i) => (
                <Link
                  key={cat.id}
                  data-index={i}
                  to={cat.link || "#"}
                  className="hover:text-green-600 transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          )}

          <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-green-950 leading-tight mb-6">
            {product.title}
          </h1>

          <div className="flex items-baseline gap-4 mb-8 pb-8 border-b border-border">
            <ProductPrice product={product} className="text-3xl md:text-4xl text-green-950 font-bold" />
          </div>

          {product.customFields?.size_guide && (
            <Accordion type="single" collapsible className="mb-8">
              <AccordionItem value="size-guide" className="border-border">
                <AccordionTrigger className="text-xs uppercase tracking-widest font-default hover:no-underline text-green-950">
                  Size Guide
                </AccordionTrigger>
                <AccordionContent>
                  <div
                    className="text-green-900 leading-relaxed [&_table]:w-full [&_th]:text-green-950 [&_td]:text-green-900"
                    dangerouslySetInnerHTML={{ __html: product.customFields.size_guide }}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {product.short_description && (
            <div
              className="text-lg text-green-900 leading-relaxed mb-8 [&_p]:mb-3 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-green-800 mb-3 font-default">
              Support This Cause
            </div>
            {product.type === 'variable' && product.attributes && Array.isArray(product.attributes) && product.attributes.length > 0 ? (
              <VariableProductAddToCart 
                product={product}
                onVariationResolved={(variation) => { setSelectedVariation(variation); setSelectedImageIndex(0); }}
                showQuantity={true}
                buttonClassName="bg-green-700 text-white rounded-lg shadow-md hover:bg-green-800"
                text="Add to Cart"
                variant="default"
                size="lg"
              />
            ) : (
              <AddToCartButton
                productId={product.id}
                variant="default"
                size="lg"
                className="w-full"
                buttonClassName="bg-green-700 text-white rounded-lg shadow-md hover:bg-green-800"
                text="Add to Cart"
                showQuantity={true}
              />
            )}
          </div>

          <div className="space-y-3 py-6 border-t border-border">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-start gap-3 text-sm">
                <Folder className="size-4 text-green-700 mt-0.5 shrink-0" />
                <div>
                  <span className="text-green-900 mr-2">Categories:</span>
                  {product.categories.map((cat, i) => (
                    <React.Fragment key={cat.id}>
                      {i > 0 && <span className="text-green-900">, </span>}
                      <Link
                        to={cat.link || "#"}
                        className="text-green-950 hover:text-green-700 transition-colors"
                      >
                        {cat.name}
                      </Link>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex items-start gap-3 text-sm">
                <Tag className="size-4 text-green-700 mt-0.5 shrink-0" />
                <div className="flex flex-wrap gap-2">
                  <span className="text-green-900">Tags:</span>
                  {product.tags.map((tag, i) => (
                    <Link
                      key={tag.id}
                      data-index={i}
                      to={tag.link || "#"}
                      className="text-green-950 hover:text-green-700 transition-colors"
                    >
                      #{tag.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {product.long_description && (
            <Accordion type="single" collapsible defaultValue="description" className="mt-4">
              <AccordionItem value="description" className="border-border">
                <AccordionTrigger className="text-xs uppercase tracking-widest font-default hover:no-underline text-green-950">
                  Full Cause Details
                </AccordionTrigger>
                <AccordionContent>
                  <div
                    className="text-green-900 leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_h3]:font-default [&_h3]:text-lg [&_h3]:text-green-950 [&_h3]:mb-2 [&_h3]:mt-4"
                    dangerouslySetInnerHTML={{ __html: product.long_description }}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </div>
      </div>

      <div className="mt-24 md:mt-32 pt-16 border-t border-border">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-12 gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-green-700 font-default mb-3">
              Collection
            </div>
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-green-950">
              Explore Other Causes
            </h2>
          </div>
          <Link to="/ShopPage">
            <Button
              variant="outline"
              className="border-green-700 text-green-950 hover:bg-green-100 rounded-lg"
            >
              View All Merch
            </Button>
          </Link>
        </div>
        <RelatedProductsConsumer currentProductId={product.id} />
      </div>
    </>
  );
}

export default function ProductDetail() {
  
  const allProductsQuery = new WP_Query({ post_type: "product", posts_per_page: 20 });

  return (
    <section data-section-id="5058"
      id="single-product-template-section"
      className="bg-slate-50 text-green-950 py-16 md:py-24 lg:py-32 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-green-700" />
            <span className="text-xs uppercase tracking-[0.3em] text-green-700 font-default">
              Forever Fresh Awareness
            </span>
            <div className="h-px w-12 bg-green-700" />
          </div>
          <p className="text-lg text-green-900 max-w-xl mx-auto">
            Keeping awareness issues forever fresh in people’s minds through purpose-driven design.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{ post_type: "product", per_page: 20 }}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}